import React, { useMemo } from 'react';
import {
  Autocomplete, Box, Chip, Divider, FormControl, FormControlLabel,
  Grid, InputLabel, MenuItem, Select, Switch, TextField, Typography
} from '@mui/material';
import FormDialog from '../../common/FormDialog';

const studName = (s) => s?.full_name || `${s?.last_name || ''} ${s?.first_name || ''}`.trim() || s?.registration_no || '';
const clsName = (c) => c?.name || c?.class_name || 'Class';
const strmName = (s) => s?.display_name || s?.name || 'Stream';
const termLabel = (t) => t ? `${t.name || 'Term'} ${t.year || ''}`.trim() : '';

const TYPES = [
  { value: 'student', label: 'Single Student' },
  { value: 'class', label: 'Entire Class Stream' },
];

const SectionLabel = ({ children }) => (
  <Typography variant="caption" fontWeight={700} color="text.secondary"
    sx={{ textTransform: 'uppercase', letterSpacing: 0.6, display: 'block', mb: 0.5 }}>
    {children}
  </Typography>
);

const GenerateReportCardDialog = ({
  open, onClose, onSubmit, saving, isMobile = false,
  form, setForm,
  terms = [], classes = [], classStreams = [], students = [], templates = [],
  allClasses = [],
}) => {
  const formStreams = useMemo(
    () => form.class_id ? classStreams.filter((s) => String(s.class_id) === String(form.class_id)) : classStreams,
    [classStreams, form.class_id]
  );

  const filteredStudents = useMemo(() => {
    let rows = students;
    if (form.class_id) rows = rows.filter((s) => String(s.class_id || s.classStream?.class_id || s.class_stream?.class_id) === String(form.class_id));
    if (form.class_stream_id) rows = rows.filter((s) => String(s.class_stream_id) === String(form.class_stream_id));
    return rows;
  }, [form.class_id, form.class_stream_id, students]);

  const selectedClass = classes.find((c) => String(c.id) === String(form.class_id)) || null;
  const selectedStream = formStreams.find((s) => String(s.id) === String(form.class_stream_id)) || null;
  const selectedTerm = terms.find((t) => String(t.id) === String(form.term_id)) || null;
  const selectedStudent = filteredStudents.find((s) => String(s.id) === String(form.student_id)) || null;

  return (
    <FormDialog
      open={open} onClose={onClose} onSubmit={onSubmit}
      title="Generate Report Cards"
      saving={saving} isMobile={isMobile} maxWidth="md"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

        {/* ── 1. Generation Type ── */}
        <Box>
          <SectionLabel>1. Generation Type</SectionLabel>
          <Autocomplete
            options={TYPES}
            value={TYPES.find((t) => t.value === form.type) || TYPES[0]}
            onChange={(_, v) => setForm((p) => ({ ...p, type: v?.value || 'student', student_id: '' }))}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            disableClearable
            renderOption={(props, o) => (
              <Box component="li" {...props} key={o.value}>
                <Box>
                  <Typography variant="body2">{o.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {o.value === 'student' ? 'Generate for one student' : 'Generate for all students in a stream'}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(p) => <TextField {...p} label="Type" size="small" />}
          />
        </Box>

        <Divider />

        {/* ── 2. Academic Period ── */}
        <Box>
          <SectionLabel>2. Academic Period</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                fullWidth size="small" type="number" label="Year"
                value={form.academic_year}
                onChange={(e) => setForm((p) => ({ ...p, academic_year: e.target.value }))}
                inputProps={{ min: 2000, max: 2100 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Autocomplete
                fullWidth options={terms} value={selectedTerm}
                onChange={(_, v) => setForm((p) => ({ ...p, term_id: v?.id || '' }))}
                getOptionLabel={termLabel}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderOption={(props, t) => (
                  <Box component="li" {...props} key={t.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2">{termLabel(t)}</Typography>
                      {(t.is_current || t.current) && <Chip label="Current" size="small" color="success" />}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Term *" size="small" />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 3. Class & Stream ── */}
        <Box>
          <SectionLabel>3. Class &amp; Stream</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth options={classes} value={selectedClass}
                onChange={(_, v) => setForm((p) => ({ ...p, class_id: v?.id || '', class_stream_id: '', student_id: '' }))}
                getOptionLabel={clsName}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(p) => <TextField {...p} label="Class *" size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                fullWidth options={formStreams} value={selectedStream}
                onChange={(_, v) => setForm((p) => ({ ...p, class_stream_id: v?.id || '', student_id: '' }))}
                getOptionLabel={strmName}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={!form.class_id}
                renderInput={(p) => <TextField {...p} label="Stream *" size="small"
                  helperText={!form.class_id ? 'Select a class first' : `${formStreams.length} stream${formStreams.length !== 1 ? 's' : ''} available`}
                />}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── 4. Template ── */}
        <Box>
          <SectionLabel>4. Template</SectionLabel>
          <FormControl fullWidth size="small">
            <InputLabel>Template</InputLabel>
            <Select
              label="Template"
              value={form.report_card_template_id}
              onChange={(e) => setForm((p) => ({ ...p, report_card_template_id: e.target.value }))}
            >
              <MenuItem value="">
                <em>Default (auto-detect from class)</em>
              </MenuItem>
              {templates.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.template_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* ── 5. Student (only for single student type) ── */}
        {form.type === 'student' && (
          <>
            <Divider />
            <Box>
              <SectionLabel>5. Select Student</SectionLabel>
              <Autocomplete
                fullWidth options={filteredStudents} value={selectedStudent}
                onChange={(_, v) => setForm((p) => ({ ...p, student_id: v?.id || '' }))}
                getOptionLabel={studName}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                disabled={!form.class_stream_id}
                renderOption={(props, s) => (
                  <Box component="li" {...props} key={s.id}>
                    <Box>
                      <Typography variant="body2">{studName(s)}</Typography>
                      {s.registration_no && (
                        <Typography variant="caption" color="text.secondary">{s.registration_no}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
                renderInput={(p) => <TextField {...p} label="Student *" size="small"
                  helperText={
                    !form.class_stream_id
                      ? 'Select class and stream first'
                      : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''} in this stream`
                  }
                />}
              />
            </Box>
          </>
        )}

        <Divider />

        {/* ── Attendance ── */}
        <Box>
          <SectionLabel>{form.type === 'student' ? '6' : '5'}. Attendance</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Days Present"
                value={form.days_present ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, days_present: e.target.value }))}
                helperText="Leave blank to skip"
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth size="small" type="number" label="Days Absent"
                value={form.days_absent ?? ''}
                onChange={(e) => setForm((p) => ({ ...p, days_absent: e.target.value }))}
                helperText="Leave blank to skip"
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* ── Promotion ── */}
        <Box>
          <SectionLabel>{form.type === 'student' ? '7' : '6'}. Promotion</SectionLabel>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: form.is_promoted ? 6 : 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(form.is_promoted)}
                    onChange={(e) => setForm((p) => ({ ...p, is_promoted: e.target.checked, promoted_to_class: '' }))}
                  />
                }
                label="Promoted"
              />
            </Grid>
            {form.is_promoted && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  fullWidth options={allClasses}
                  value={allClasses.find((c) => clsName(c) === form.promoted_to_class) || null}
                  onChange={(_, v) => setForm((p) => ({ ...p, promoted_to_class: v ? clsName(v) : '' }))}
                  getOptionLabel={clsName}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => <TextField {...p} label="Promoted To Class" size="small" placeholder="Select class..." />}
                />
              </Grid>
            )}
          </Grid>
        </Box>

      </Box>
    </FormDialog>
  );
};

export default GenerateReportCardDialog;
